import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import InvoiceCreateWizard from "./InvoiceCreateWizard";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import { buildMultipartFormDataRequestConfig } from "../../utilis/financeInvoicePaymentHelpers";
import {
  buildFinanceInvoicesReturnUrl,
  buildInvoiceCreateRequestBody,
  getFinanceInvoiceApiMessage,
  isFinanceInvoiceApiSuccess,
  mapFinanceInvoicePayloadTaxTypesForApi,
  readFinanceInvoiceListTabsFromLocation,
} from "../../utilis/invoiceCreateHelpers";
import {
  getInvoiceStatusLabel,
  normalizeInvoiceStatus,
} from "../../utilis/invoiceStatusHelpers";
import {
  applyFinanceInvoiceMutationFromResponse,
  normalizeFinanceInvoiceFromRecord,
  normalizeFinanceInvoiceRecord,
  normalizeInvoiceNotesList,
} from "../../utilis/invoiceEditHelpers";
import {
  buildFinanceInvoicePaymentFormData,
  getFinanceInvoicePaymentApiMessage,
  isFinanceInvoicePaymentApiSuccess,
  recordFinanceInvoicePaymentsSequentially,
} from "../../utilis/financeInvoicePaymentHelpers";
import {
  applyCommissionApproveFields,
  sumCommissionPayloadPaidAmount,
} from "../reports/commissions/commissionInvoiceApproveHelpers";
import { mapCommissionPaymentsFromPayload } from "../reports/commissions/commissionInvoicePaymentHelpers";
import {
  showCommissionInvoiceSuccessToast,
} from "../reports/commissions/commissionInvoiceToast";
import {
  buildInvoiceNumberWithClientId,
  extractNextInvoiceNumberFromResponse,
  fetchNextInvoiceSequence,
  incrementInvoiceNumber,
} from "../../utilis/invoiceNumberHelpers";
import { canViewApplicationInvoices } from "../../utilis/invoicePermissionHelpers";
import {
  applyInvoiceCatalog,
  fetchApplicationInvoiceDetail,
  fetchInvoiceCatalog,
  fetchInvoiceNoteList,
  workspaceErrorMessage,
} from "../../utilis/invoiceWorkspaceApi";
import {
  buildExternalAgentDisplayProfile,
  buildExternalAgentLabelMap,
  buildMixedContactSearchSuggestions,
  resolveClientExternalAgentIds,
  resolveExternalAgentRecord,
  resolveInvoiceExternalAgentIds,
} from "../../utilis/invoiceExternalAgentHelpers";
import { fetchExternalAgents } from "../reports/commissions/commissionsApi";
import "./../Clients/clients.css";

// NOTE: You said you'll create new APIs for "user search" + "fetch applications".
// The UI below calls a placeholder endpoint for user search.
// Update `USER_SEARCH_ENDPOINT` + `normalizeUserSearchResults` if your backend differs.
const USER_SEARCH_ENDPOINT = "searchClients";

function normalizeUserSearchResults(response) {
  const payload =
    response?.data?.data ??
    response?.data?.results ??
    response?.data ??
    null;

  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : [];

  return list.map((item) => {
    const id = item?.id ?? item?.client_id ?? item?.clientId ?? null;
    const label =
      item?.full_name ??
      item?.fullName ??
      item?.name ??
      item?.client_name ??
      item?.clientName ??
      "—";
    const email =
      item?.email ?? item?.client_email ?? item?.clientEmail ?? "";

    return { id, label, email, raw: item, kind: "client" };
  });
}

function resolveClientDisplayName(record) {
  if (!record) return "";
  return (
    record?.fullName ??
    record?.full_name ??
    record?.name ??
    record?.client_name ??
    record?.clientName ??
    ""
  );
}

function resolveFinanceCreatedInvoiceId(response) {
  const data = response?.data?.data ?? response?.data ?? {};
  return data?.id ?? data?.invoice?.id ?? null;
}

function FinanceCreateInvoice() {
  const navigate = useNavigate();
  const location = useLocation();
  const financeListReturnTabs = useMemo(
    () => readFinanceInvoiceListTabsFromLocation(location),
    [location],
  );
  const financeListReturnUrl = useMemo(
    () => buildFinanceInvoicesReturnUrl(financeListReturnTabs),
    [financeListReturnTabs],
  );

  const [globalLoading, setGlobalLoading] = useState(true);
  const [clientLoading, setClientLoading] = useState(false);

  const [clientData, setClientData] = useState(null);
  const [invoiceSetting, setInvoiceSetting] = useState(null);
  const [discountFeeRules, setDiscountFeeRules] = useState([]);
  const [existingInvoicesForNumbering, setExistingInvoicesForNumbering] = useState(
    [],
  );
  const [clientApplications, setClientApplications] = useState([]);
  const [clientFinanceInvoices, setClientFinanceInvoices] = useState([]);
  const [nextInvoiceSequence, setNextInvoiceSequence] = useState("");

  const [saving, setSaving] = useState(false);
  const [activeInvoice, setActiveInvoice] = useState(null);
  const [invoiceNotes, setInvoiceNotes] = useState([]);
  const [loadingInvoiceNotes, setLoadingInvoiceNotes] = useState(false);
  const [addingInvoiceNote, setAddingInvoiceNote] = useState(false);
  const [savingFinancePayment, setSavingFinancePayment] = useState(false);
  const [reversingFinancePayment] = useState(false);

  // "User" typeahead (your requirement)
  const [userQuery, setUserQuery] = useState("");
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [agentLabelMap, setAgentLabelMap] = useState(() => new Map());
  const [referringAgents, setReferringAgents] = useState([]);
  const [processingAgents, setProcessingAgents] = useState([]);
  const [selectedReferringAgentId, setSelectedReferringAgentId] = useState("");
  const [selectedProcessingAgentId, setSelectedProcessingAgentId] = useState("");
  const skipNextSearchRef = useRef(false);
  const [invoiceCatalog, setInvoiceCatalog] = useState(null);
  const clientLoadControllerRef = useRef(null);

  const selectedClientIdString = useMemo(
    () => (selectedClientId ? String(selectedClientId) : ""),
    [selectedClientId],
  );

  const agencyBillToProfile = useMemo(() => {
    if (selectedClientId) return null;

    const agentId = selectedReferringAgentId || selectedProcessingAgentId;
    if (!agentId) return null;

    const agent = resolveExternalAgentRecord(
      agentId,
      referringAgents,
      processingAgents,
    );
    if (!agent) return null;

    return buildExternalAgentDisplayProfile(agent);
  }, [
    selectedClientId,
    selectedReferringAgentId,
    selectedProcessingAgentId,
    referringAgents,
    processingAgents,
  ]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const loadGlobal = async () => {
      setGlobalLoading(true);
      try {
        const [catalogResult, nextInvoiceResult] = await Promise.allSettled([
          fetchInvoiceCatalog({ signal: controller.signal }),
          apiUrl.get("getNextInvoiceNumber", { signal: controller.signal }),
        ]);

        if (cancelled) return;

        if (catalogResult.status === "fulfilled") {
          const catalog = catalogResult.value || {};
          setInvoiceCatalog(catalog);
          const mapped = applyInvoiceCatalog(catalog);
          setInvoiceSetting(mapped.setting);
          setDiscountFeeRules(mapped.discountRules);
        }

        if (nextInvoiceResult.status === "fulfilled") {
          setNextInvoiceSequence(
            extractNextInvoiceNumberFromResponse(nextInvoiceResult.value),
          );
        } else {
          setNextInvoiceSequence("");
        }
      } catch (e) {
        if (cancelled) return;
        console.error("Failed to load invoice defaults", e);
      } finally {
        if (!cancelled) setGlobalLoading(false);
      }
    };

    loadGlobal();
    return () => {
      cancelled = true;
      controller.abort();
      clientLoadControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    fetchExternalAgents().then(({ processing, referring }) => {
      if (cancelled) return;
      setReferringAgents(Array.isArray(referring) ? referring : []);
      setProcessingAgents(Array.isArray(processing) ? processing : []);
      setAgentLabelMap(buildExternalAgentLabelMap(referring, processing));
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const applyAgentIds = useCallback((referringId = "", processingId = "") => {
    setSelectedReferringAgentId(referringId ? String(referringId) : "");
    setSelectedProcessingAgentId(processingId ? String(processingId) : "");
  }, []);

  const applyAgentSelectionFromClient = useCallback(
    (clientRecord) => {
      const { referring, processing } =
        resolveClientExternalAgentIds(clientRecord);
      applyAgentIds(referring, processing);
    },
    [applyAgentIds],
  );

  const applyAgentSelectionFromInvoice = useCallback(
    (invoiceRecord) => {
      const fromInvoice = resolveInvoiceExternalAgentIds(invoiceRecord);
      if (fromInvoice.referring || fromInvoice.processing) {
        applyAgentIds(fromInvoice.referring, fromInvoice.processing);
        return;
      }
      applyAgentSelectionFromClient(clientData);
    },
    [applyAgentIds, applyAgentSelectionFromClient, clientData],
  );

  const clearAgentSelection = useCallback(() => {
    applyAgentIds("", "");
  }, [applyAgentIds]);

  // Typeahead API call
  useEffect(() => {
    const run = async () => {
      if (skipNextSearchRef.current) {
        skipNextSearchRef.current = false;
        setUserSearchLoading(false);
        setShowSuggestions(false);
        setUserSuggestions([]);
        return;
      }

      const q = userQuery.trim();
      if (!q || q.length < 2) {
        setUserSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      setUserSearchLoading(true);
      let clientItems = [];

      try {
        const res = await apiUrl.get(USER_SEARCH_ENDPOINT, {
          params: { query: q },
        });
        clientItems = normalizeUserSearchResults(res);
      } catch (e) {
        console.error("User search failed", e);
        if (/^\d+$/.test(q)) {
          try {
            const detailRes = await apiUrl.get(`viewClientDetails/${q}`);
            const record = detailRes?.data?.data;
            if (record) {
              const id =
                record?.id ?? record?.client_id ?? record?.clientId ?? q;
              const label = resolveClientDisplayName(record) || "—";
              const email =
                record?.email ?? record?.client_email ?? record?.clientEmail ?? "";
              clientItems = [{ id, label, email, raw: record, kind: "client" }];
            }
          } catch (fallbackError) {
            console.error("Fallback client lookup failed", fallbackError);
          }
        }
      }

      const merged = buildMixedContactSearchSuggestions({
        clients: clientItems,
        referringAgents,
        processingAgents,
        query: q,
      });
      setUserSuggestions(merged);
      setShowSuggestions(merged.length > 0 || q.length >= 2);
      setUserSearchLoading(false);
    };

    // Simple debounce
    const t = window.setTimeout(run, 350);
    return () => window.clearTimeout(t);
  }, [userQuery, referringAgents, processingAgents]);

  const loadClientSpecificData = async (clientId) => {
    clientLoadControllerRef.current?.abort();
    const controller = new AbortController();
    clientLoadControllerRef.current = controller;
    setClientLoading(true);
    try {
      const workspace = await fetchApplicationInvoiceDetail({
        clientId,
        signal: controller.signal,
      });

      const record = workspace.client || null;
      setClientData(record);
      if (record) {
        applyAgentSelectionFromClient(record);
      } else {
        clearAgentSelection();
      }

      const siblingList = Array.isArray(workspace.sibling_invoice_numbers)
        ? workspace.sibling_invoice_numbers
        : [];
      setExistingInvoicesForNumbering(siblingList);
      setClientFinanceInvoices(siblingList);
      setClientApplications(
        Array.isArray(workspace.applications) ? workspace.applications : [],
      );
    } catch (e) {
      if (workspaceErrorMessage(e, null) === null) return;
      console.error("Failed loading client invoice create data", e);
      setClientData(null);
      clearAgentSelection();
      setExistingInvoicesForNumbering([]);
      setClientFinanceInvoices([]);
      setClientApplications([]);
    } finally {
      setClientLoading(false);
    }
  };

  const handlePickUser = async (suggestion) => {
    if (!suggestion?.id) return;
    const nextId = suggestion.id;

    skipNextSearchRef.current = true;
    setSelectedClientId(nextId);
    setUserQuery(suggestion.label);
    setShowSuggestions(false);
    setUserSuggestions([]);

    await loadClientSpecificData(nextId);
  };

  const handlePickContact = async (suggestion) => {
    if (!suggestion) return;

    const kind = suggestion.kind || "client";
    if (kind === "client") {
      await handlePickUser(suggestion);
      return;
    }

    skipNextSearchRef.current = true;
    const agentId = String(suggestion.id);

    if (kind === "referring") {
      setSelectedReferringAgentId(agentId);
    } else if (kind === "processing") {
      setSelectedProcessingAgentId(agentId);
    } else {
      setSelectedReferringAgentId(agentId);
      setSelectedProcessingAgentId(agentId);
    }

    setShowSuggestions(false);
    setUserSuggestions([]);

    if (selectedClientId && clientData) {
      setUserQuery(resolveClientDisplayName(clientData) || suggestion.label);
      return;
    }

    setUserQuery(suggestion.label || "");
  };

  const handleClose = () => {
    navigate(financeListReturnUrl);
  };

  const commissionShellStatus = useMemo(() => {
    if (activeInvoice?.approved) return "approved";
    if (activeInvoice?.status) {
      return normalizeInvoiceStatus(activeInvoice.status);
    }
    return "draft";
  }, [activeInvoice?.approved, activeInvoice?.status]);

  const isDraftLikeFlow = !activeInvoice?.id;

  const handleSaveComplete = useCallback(() => {
    navigate(
      buildFinanceInvoicesReturnUrl({
        statusTab: "draft",
        moduleTab: financeListReturnTabs.moduleTab,
      }),
    );
  }, [navigate, financeListReturnTabs.moduleTab]);

  const fetchInvoiceNotes = useCallback(async (targetInvoiceId, { silent = false } = {}) => {
    if (!targetInvoiceId) {
      setInvoiceNotes([]);
      return [];
    }
    try {
      if (!silent) setLoadingInvoiceNotes(true);
      const notes = normalizeInvoiceNotesList({
        data: {
          success: true,
          data: await fetchInvoiceNoteList(targetInvoiceId, {
            module: "application",
          }),
        },
      });
      setInvoiceNotes(notes);
      return notes;
    } catch (error) {
      console.error("Failed to load invoice notes", error);
      return [];
    } finally {
      if (!silent) setLoadingInvoiceNotes(false);
    }
  }, []);

  const refreshActiveInvoiceRecord = useCallback(async (targetInvoiceId) => {
    if (!targetInvoiceId) return null;
    try {
      const response = await apiUrl.get(`clientFinanceInvoices/${targetInvoiceId}`);
      if (isFinanceInvoiceApiSuccess(response)) {
        const mapped = normalizeFinanceInvoiceRecord(response);
        const refreshed = mapped
          ? {
              ...mapped,
              approved: Boolean(mapped?.approved ?? activeInvoice?.approved),
            }
          : null;
        setActiveInvoice(refreshed);
        if (refreshed) {
          applyAgentSelectionFromInvoice(refreshed);
        }
        return refreshed;
      }
    } catch (error) {
      console.error("Failed to refresh finance invoice", error);
    }
    return null;
  }, [activeInvoice?.approved, applyAgentSelectionFromInvoice]);

  const handleAddFinanceInvoicePayment = async (
    payment,
    receiptFile = null,
    receiptFiles = null,
    options = {},
  ) => {
    const { silent = false, skipNavigate = false } = options;
    const targetInvoiceId = activeInvoice?.id;
    if (!targetInvoiceId) {
      Swal.fire("Error", "Invoice details are missing.", "error");
      return false;
    }

    try {
      setSavingFinancePayment(true);
      const formData = buildFinanceInvoicePaymentFormData({
        invoiceId: targetInvoiceId,
        clientId: selectedClientIdString,
        payment,
        receiptFile,
        receiptFiles,
      });

      const response = await apiUrl.post(
        `invoicepayment/${targetInvoiceId}`,
        formData,
        buildMultipartFormDataRequestConfig(),
      );

      if (!isFinanceInvoicePaymentApiSuccess(response)) {
        Swal.fire(
          "Error",
          getFinanceInvoicePaymentApiMessage(response, "Failed to record payment."),
          "error",
        );
        return false;
      }

      setActiveInvoice((prev) => {
        const next = applyFinanceInvoiceMutationFromResponse(
          prev || normalizeFinanceInvoiceFromRecord({ id: targetInvoiceId }),
          response,
        );
        return {
          ...next,
          approved: Boolean(next?.approved ?? prev?.approved),
        };
      });
      const refreshed = await refreshActiveInvoiceRecord(targetInvoiceId);
      const refreshedStatus = normalizeInvoiceStatus(
        refreshed?.status || activeInvoice?.status,
      );

      if (refreshedStatus === "paid") {
        showCommissionInvoiceSuccessToast(
          getFinanceInvoicePaymentApiMessage(response, "Invoice paid in full."),
        );
      } else if (!silent) {
        showCommissionInvoiceSuccessToast(
          getFinanceInvoicePaymentApiMessage(
            response,
            "Payment recorded successfully.",
          ),
        );
      }

      if (!skipNavigate) {
        navigate(financeListReturnUrl);
      }
      return true;
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Failed to record payment.",
        "error",
      );
      return false;
    } finally {
      setSavingFinancePayment(false);
    }
  };

  const handleAddFinanceInvoiceBulkPayment = async (entries) => {
    const saved = await recordFinanceInvoicePaymentsSequentially(
      entries,
      (payment, receiptFile, receiptFiles, options = {}) =>
        handleAddFinanceInvoicePayment(payment, receiptFile, receiptFiles, {
          ...options,
          skipNavigate: true,
        }),
    );

    if (saved) {
      navigate(financeListReturnUrl);
    }

    return saved;
  };

  const handleAddInvoiceNote = async (noteText) => {
    const details = String(noteText || "").trim();
    if (!details) {
      Swal.fire("Warning", "Please enter a note.", "warning");
      return false;
    }

    const targetInvoiceId = activeInvoice?.id;
    const invoiceNumber = activeInvoice?.invoice_number;
    if (!targetInvoiceId || !invoiceNumber) {
      Swal.fire("Error", "Invoice details are missing.", "error");
      return false;
    }

    try {
      setAddingInvoiceNote(true);
      const response = await apiUrl.post("addnoteforinvoice", {
        invoice_id: targetInvoiceId,
        invoice_number: invoiceNumber,
        details,
      });

      if (response?.data?.success || response?.data?.status) {
        await fetchInvoiceNotes(targetInvoiceId);
        return true;
      }

      Swal.fire(
        "Error",
        response?.data?.error ||
          response?.data?.message ||
          "Failed to add note.",
        "error",
      );
      return false;
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Failed to add note.",
        "error",
      );
      return false;
    } finally {
      setAddingInvoiceNote(false);
    }
  };

  const handleCreate = async (payload, options = {}) => {
    if (!selectedClientIdString) {
      Swal.fire("Missing client", "Select a user/client to create the invoice.", "warning");
      return;
    }

    setSaving(true);
    try {
      let currentPayload = mapFinanceInvoicePayloadTaxTypesForApi({ ...payload });
      if (options?.approve) {
        currentPayload = applyCommissionApproveFields(currentPayload, {
          totalAmount: payload?.total_amount,
          paidAmount: sumCommissionPayloadPaidAmount(
            payload,
            payload?.payments || (payload?.payment ? [payload.payment] : []),
          ),
        });
      }

      let lastError = "Unable to create invoice.";

      for (let attempt = 0; attempt < 6; attempt += 1) {
        const { body, isFormData } = buildInvoiceCreateRequestBody(currentPayload);
        const response = await apiUrl.post("savefinanceinvoice", body, {
          ...(isFormData ? buildMultipartFormDataRequestConfig() : {}),
        });

        if (isFinanceInvoiceApiSuccess(response)) {
          const statusLabel = getInvoiceStatusLabel(currentPayload?.status);
          showCommissionInvoiceSuccessToast(
            options?.approve
              ? "Invoice approved."
              : `Invoice saved as ${statusLabel}.`,
          );

          const createdInvoiceId = resolveFinanceCreatedInvoiceId(response);
          if (options?.approve && createdInvoiceId) {
            await refreshActiveInvoiceRecord(createdInvoiceId);
            await fetchInvoiceNotes(createdInvoiceId, { silent: true });
            return { success: true, stayOnPage: true, invoiceId: createdInvoiceId };
          }

          if (options?.openApproveEmailModal && createdInvoiceId) {
            await refreshActiveInvoiceRecord(createdInvoiceId);
            return {
              success: true,
              stayOnPage: true,
              invoiceId: createdInvoiceId,
            };
          }

          if (options?.saveAndAnother) {
            const nextTemplate = await fetchNextInvoiceSequence(apiUrl);
            setNextInvoiceSequence(nextTemplate || "");
            return { success: true, saveAndAnother: true, stayOnPage: true };
          }

          return { success: true, stayOnPage: false };
        }

        lastError = getFinanceInvoiceApiMessage(
          response,
          "Unable to create invoice.",
        );

        if (!/already taken|already exists|duplicate/i.test(lastError) || attempt >= 5) {
          throw new Error(lastError);
        }

        // Retry with next invoice sequence.
        const nextTemplate = await fetchNextInvoiceSequence(apiUrl);
        const nextInvoiceNumber = nextTemplate
          ? buildInvoiceNumberWithClientId({
              invoiceNumberTemplate: nextTemplate,
              clientId: selectedClientIdString,
              documentType: "invoice",
            })
          : incrementInvoiceNumber(currentPayload.invoice_number);

        currentPayload = {
          ...currentPayload,
          invoice_number: nextInvoiceNumber,
        };
      }

      throw new Error(lastError);
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to create invoice.",
        "error",
      );
      return { success: false };
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async ({ id, payload }, options = {}) => {
    const targetId = id || activeInvoice?.id;
    if (!targetId) return false;

    setSaving(true);
    try {
      const isApprove = Boolean(options?.approve);
      const payments = mapCommissionPaymentsFromPayload(
        payload,
        payload?.line_items || [],
      );

      let requestPayload = mapFinanceInvoicePayloadTaxTypesForApi({ ...payload });
      if (payments.length > 0) {
        requestPayload.payments = payments;
        if (payments.length === 1) {
          requestPayload.payment = payments[0];
        }
      }

      if (isApprove) {
        requestPayload = applyCommissionApproveFields(requestPayload, {
          totalAmount: payload?.total_amount,
          paidAmount: sumCommissionPayloadPaidAmount(payload, payments),
        });
      }

      const { body, isFormData } = buildInvoiceCreateRequestBody(requestPayload);
      const response = await apiUrl.post(`editfinanceinvoice/${targetId}`, body, {
        ...(isFormData ? buildMultipartFormDataRequestConfig() : {}),
      });

      if (!isFinanceInvoiceApiSuccess(response)) {
        throw new Error(
          getFinanceInvoiceApiMessage(response, "Unable to update invoice."),
        );
      }

      if (isApprove) {
        showCommissionInvoiceSuccessToast("Invoice approved.");
        await refreshActiveInvoiceRecord(targetId);
        await fetchInvoiceNotes(targetId, { silent: true });
        if (options?.saveAndAnother) {
          return { success: true, saveAndAnother: true, stayOnPage: true };
        }
        return { success: true, stayOnPage: true };
      }

      showCommissionInvoiceSuccessToast("Invoice updated.");
      await refreshActiveInvoiceRecord(targetId);
      return { success: true, stayOnPage: true };
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to update invoice.",
        "error",
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (!canViewApplicationInvoices()) {
    return (
      <div className="client-invoices-xero client-create-invoice-page p-4">
        <p className="text-muted mb-0">
          You do not have permission to view application invoices.
        </p>
      </div>
    );
  }

  return (
    <div className="client-invoices-xero client-create-invoice-page">
      <div className="client-invoices-xero__breadcrumb">
        <Link to={financeListReturnUrl}>Invoices</Link>
        <span aria-hidden="true">›</span>
        <span>New invoice</span>
      </div>

      <InvoiceCreateWizard
        show
        pageMode
        pageLoading={globalLoading}
        commissionShellStatus={commissionShellStatus}
        onClose={handleClose}
        onSaveComplete={isDraftLikeFlow ? handleSaveComplete : undefined}
        onCreate={isDraftLikeFlow ? handleCreate : undefined}
        onUpdate={activeInvoice ? handleUpdate : undefined}
        onInvoiceEmailSent={() => {
          const targetId = activeInvoice?.id;
          if (targetId) fetchInvoiceNotes(targetId, { silent: true });
        }}
        existingInvoice={activeInvoice || undefined}
        clientData={clientData}
        billToProfile={agencyBillToProfile}
        invoiceSetting={invoiceSetting}
        invoiceCatalog={invoiceCatalog}
        saving={saving}
        discountFeeRules={discountFeeRules}
        existingInvoicesForNumbering={existingInvoicesForNumbering}
        clientApplications={clientApplications}
        clientFinanceInvoices={clientFinanceInvoices}
        nextInvoiceSequence={nextInvoiceSequence}
        loadingNextInvoiceNumber={false}
        invoiceDataLoadedFromPage
        onAddInvoiceNote={activeInvoice ? handleAddInvoiceNote : undefined}
        addingInvoiceNote={addingInvoiceNote}
        invoiceNotes={invoiceNotes}
        loadingInvoiceNotes={loadingInvoiceNotes}
        onHistoryVisible={
          activeInvoice?.id
            ? () => fetchInvoiceNotes(activeInvoice.id)
            : undefined
        }
        onSaveFinanceInvoicePayment={
          activeInvoice ? handleAddFinanceInvoicePayment : undefined
        }
        onSaveFinanceInvoiceBulkPayment={
          activeInvoice ? handleAddFinanceInvoiceBulkPayment : undefined
        }
        savingFinanceInvoicePayment={savingFinancePayment}
        onReverseFinanceInvoicePayment={null}
        reversingFinanceInvoicePayment={reversingFinancePayment}
        enableContactSearch
        contactSearchValue={userQuery}
        contactSearchLoading={showSuggestions && userSearchLoading}
        contactSearchResults={showSuggestions ? userSuggestions : []}
        contactReferringAgentId={selectedReferringAgentId}
        contactProcessingAgentId={selectedProcessingAgentId}
        onContactSearchChange={(value) => {
          setUserQuery(value);
          setSelectedClientId(null);
          setClientData(null);
          setClientApplications([]);
          setExistingInvoicesForNumbering([]);
          setClientFinanceInvoices([]);
          clearAgentSelection();
        }}
        onContactSearchFocus={() => {
          if (userQuery.trim().length >= 2) setShowSuggestions(true);
        }}
        onContactSearchBlur={() => {
          window.setTimeout(() => setShowSuggestions(false), 150);
        }}
        onContactSearchSelect={handlePickContact}
        softLoading={clientLoading}
      />
    </div>
  );
}

export default FinanceCreateInvoice;

